import React, { createContext, useContext, useEffect, useState } from 'react';
import { DEFAULT_FEATURES, Features } from '../config/features';
import apiService from '../services/api';

type FeaturesContextValue = Features & {
  ready: boolean;
};

const FeaturesContext = createContext<FeaturesContextValue>({
  ...DEFAULT_FEATURES,
  ready: false,
});

function mergeRemoteConfig(remote: unknown): Features {
  const next: Features = { ...DEFAULT_FEATURES };
  if (!remote || typeof remote !== 'object') return next;
  const value = (remote as { dozynki?: unknown }).dozynki;
  if (typeof value === 'boolean') {
    next.dozynki = value;
  }
  return next;
}

export function FeaturesProvider({ children }: { children: React.ReactNode }) {
  const [features, setFeatures] = useState<Features>(DEFAULT_FEATURES);
  const [ready, setReady] = useState(false);

  useEffect(() => {
    let cancelled = false;

    (async () => {
      try {
        const remote = await apiService.getConfig();
        if (!cancelled) {
          setFeatures(mergeRemoteConfig(remote));
        }
      } catch {
        if (!cancelled) {
          setFeatures(DEFAULT_FEATURES);
        }
      } finally {
        if (!cancelled) setReady(true);
      }
    })();

    return () => {
      cancelled = true;
    };
  }, []);

  return (
    <FeaturesContext.Provider value={{ ...features, ready }}>
      {children}
    </FeaturesContext.Provider>
  );
}

export function useFeatures(): FeaturesContextValue {
  return useContext(FeaturesContext);
}
